<template>
  <div class="yuebao-page">
    <header class="hero">
      <div class="nav">
        <button
          type="button"
          class="back"
          :aria-label="$t('yuebao.back')"
          @click="$router.back()"
        >
          ‹
        </button><h1>{{ $t('yuebao.title') }}</h1><button
          type="button"
          class="orders-link"
          @click="$router.push('/yuebao/orders')"
        >
          {{ $t('yuebao.orders') }}
        </button>
      </div>
      <p class="balance-label">
        {{ $t('yuebao.totalBalance') }}
      </p>
      <p class="balance">
        {{ $t('utils.moneyMark') }}{{ money(account.total_assets) }}
      </p>
      <div class="income-row">
        <div><strong>{{ money(account.total_income) }}</strong><span>{{ $t('yuebao.totalIncome') }}</span></div>
        <div><strong>{{ money(account.today_income) }}</strong><span>{{ $t('yuebao.todayIncome') }}</span></div>
      </div>
    </header>

    <main>
      <section :class="['panel', 'operation-panel', 'mode-' + mode]">
        <div class="mode-tabs">
          <button
            type="button"
            :class="{active: mode === 'in'}"
            @click="changeMode('in')"
          >
            {{ $t('yuebao.transferIn') }}
          </button>
          <button
            type="button"
            :class="{active: mode === 'out'}"
            @click="changeMode('out')"
          >
            {{ $t('yuebao.transferOut') }}
          </button>
        </div>

        <template v-if="mode === 'in'">
          <button
            v-if="selectedProduct"
            type="button"
            class="product-selector"
            :aria-label="$t('yuebao.switchProduct')"
            @click="productSheetShow = true"
          >
            <strong>{{ productName(selectedProduct) }}</strong>
            <span class="product-summary">{{ $t('yuebao.annualShort') }}{{ rate(selectedProduct) }} · {{ term(selectedProduct) }} · {{ $t('yuebao.dailyInterest') }}</span>
            <i>›</i>
          </button>
          <button
            v-else-if="products.length"
            type="button"
            class="product-selector product-selector-empty"
            @click="productSheetShow = true"
          >
            <strong>{{ $t('yuebao.selectProduct') }}</strong><i>›</i>
          </button>
          <div
            v-else
            class="empty-products"
          >
            {{ $t('yuebao.noProducts') }}
          </div>
        </template>

        <div class="amount-head">
          <span>{{ $t('yuebao.amount') }}</span><span>{{ mode === 'in' ? $t('yuebao.walletAvailable') : $t('yuebao.yuebaoAvailable') }}：{{ $t('utils.moneyMark') }}{{ money(availableBalance) }}</span>
        </div>
        <div class="amount-input">
          <span>{{ $t('utils.moneyMark') }}</span><input
            :value="amount"
            inputmode="decimal"
            :placeholder="$t('yuebao.amountPlaceholder')"
            @input="onAmountInput"
          ><button
            type="button"
            @click="fillAll"
          >
            {{ $t('yuebao.all') }}
          </button>
        </div>
        <p class="hint">
          {{ $t('yuebao.amountHint') }}
        </p>
        <button
          type="button"
          :class="['submit', 'submit-' + mode]"
          :disabled="submitting || (mode === 'in' && !products.length)"
          @click="submit"
        >
          {{ submitting ? $t('yuebao.submitting') : (mode === 'in' ? $t('yuebao.confirmIn') : $t('yuebao.confirmOut')) }}
        </button>
      </section>

      <section class="panel holding">
        <div><span>{{ $t('yuebao.holdingPrincipal') }}</span><strong>{{ $t('utils.moneyMark') }}{{ money(holdingPrincipal) }}</strong></div>
        <button
          type="button"
          @click="$router.push('/yuebao/orders')"
        >
          {{ $t('yuebao.viewOrders') }} ›
        </button>
      </section>

      <section class="panel records-panel">
        <div class="section-title">
          <span>{{ $t('yuebao.recentRecords') }}</span>
        </div>
        <template v-if="records.length">
          <div
            v-for="(record, index) in records"
            :key="record.id || record.record_no || index"
            class="record"
          >
            <div><strong>{{ recordTitle(record) }}</strong><span>{{ display(record.created_at || record.time || record.create_time) }}</span></div>
            <b :class="['1', '2', '3', '4'].includes(String(record.event_type)) ? 'plus' : ''">{{ recordAmount(record) }}</b>
          </div>
        </template>
        <p
          v-if="!records.length"
          class="empty-records"
        >
          {{ $t('yuebao.noRecords') }}
        </p>
      </section>

      <section class="rules">
        <h2>{{ $t('yuebao.rulesTitle') }}</h2>
        <p>{{ $t('yuebao.rule1') }}</p><p>{{ $t('yuebao.rule2') }}</p><p>{{ $t('yuebao.rule3') }}</p>
      </section>
    </main>

    <van-action-sheet
      v-model="productSheetShow"
      :title="$t('yuebao.switchProduct')"
      :cancel-text="$t('utils.cancel')"
    >
      <div class="product-sheet-list">
        <button
          v-for="product in products"
          :key="productKey(product)"
          type="button"
          class="product-option"
          :class="{selected: selectedProductId === productKey(product)}"
          @click="selectProduct(product)"
        >
          <strong>{{ productName(product) }}</strong>
          <span>{{ $t('yuebao.annualRate') }}{{ rate(product) }} · {{ term(product) }}</span>
          <i v-if="selectedProductId === productKey(product)">✓</i>
        </button>
      </div>
    </van-action-sheet>
  </div>
</template>

<script>
import Vue from 'vue';
import { ActionSheet } from 'vant';
import Fetch from '../../../utils/fetch';

Vue.use(ActionSheet);

export default {
  name: 'Yuebao',
  data() {
    return {
      account: {}, userMoney: 0, holdingPrincipal: 0, products: [], records: [],
      selectedProductId: '', productSheetShow: false, mode: 'in', amount: '', submitting: false, requestNo: this.createRequestNo()
    };
  },
  computed: {
    availableBalance() { return this.mode === 'in' ? this.userMoney : this.account.balance; },
    selectedProduct() { return this.products.find(item => this.productKey(item) === this.selectedProductId); }
  },
  created() { this.$parent.footer('user', false); },
  mounted() { this.loadData(); this.loadRecords(); },
  methods: {
    loadData() {
      Fetch('/yuebao/index').then(r => {
        const data = r.data || {};
        this.account = data.account || {};
        this.userMoney = data.user_money == null ? 0 : data.user_money;
        this.holdingPrincipal = data.holding_principal == null ? 0 : data.holding_principal;
        this.products = Array.isArray(data.products) ? data.products : [];
        if (!this.products.some(item => this.productKey(item) === this.selectedProductId)) this.selectedProductId = this.products.length ? this.productKey(this.products[0]) : '';
      }).catch(() => {});
    },
    loadRecords() {
      Fetch('/yuebao/records', { page: 1, limit: 5 }, {}, false).then(r => {
        const data = r.data || {};
        this.records = Array.isArray(data) ? data : (data.list || data.records || []);
      }).catch(() => { this.records = []; });
    },
    createRequestNo() { return 'YB' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10); },
    changeMode(mode) { if (this.mode === mode) return; this.mode = mode; this.amount = ''; this.requestNo = this.createRequestNo(); },
    onAmountInput(event) {
      let value = String(event.target.value || '').replace(/[^\d.]/g, '');
      const dot = value.indexOf('.');
      if (dot !== -1) value = value.slice(0, dot + 1) + value.slice(dot + 1).replace(/\./g, '').slice(0, 2);
      value = value.replace(/^0+(?=\d)/, '0');
      this.amount = value;
      event.target.value = value;
    },
    fillAll() { this.amount = this.money(this.availableBalance); },
    selectProduct(product) { this.selectedProductId = this.productKey(product); this.productSheetShow = false; },
    submit() {
      if (this.submitting) return;
      if (this.mode === 'in' && !this.selectedProduct) return this.$toast(this.$t('yuebao.chooseProductTip'));
      if (!/^\d+(\.\d{1,2})?$/.test(this.amount) || Number(this.amount) <= 0) return this.$toast(this.$t('yuebao.invalidAmount'));
      if (Number(this.amount) > Number(this.availableBalance || 0)) return this.$toast(this.$t('yuebao.insufficientBalance'));
      const path = this.mode === 'in' ? '/yuebao/transfer_in' : '/yuebao/transfer_out';
      const payload = { amount: this.amount, request_no: this.requestNo };
      if (this.mode === 'in') payload.product_id = this.selectedProductId;
      this.submitting = true;
      Fetch(path, payload).then(() => {
        this.$toast({ background: '#07c160', message: this.$t('yuebao.success') });
        this.amount = '';
        this.requestNo = this.createRequestNo();
        this.loadData(); this.loadRecords();
      }).catch(() => {}).finally(() => { this.submitting = false; });
    },
    productKey(product) { const value = product.id == null ? product.product_id : product.id; return value == null ? '' : String(value); },
    productName(product) { return this.display(product.name || product.product_name, this.$t('yuebao.unnamedProduct')); },
    rate(product) { const value = product.annual_rate == null ? (product.rate == null ? product.apr : product.rate) : product.annual_rate; return value == null || value === '' ? '--' : String(value).replace(/%$/, '') + '%'; },
    term(product) { return product.term_days == null || product.term_days === '' ? '--' : product.term_days + this.$t('utils.day'); },
    money(value) { const number = Number(value); return Number.isFinite(number) ? number.toFixed(2) : '0.00'; },
    display(value, fallback) { return value == null || value === '' ? (fallback || '--') : value; },
    recordTitle(record) { return ({ '0': this.$t('yuebao.eventTransferIn'), '1': this.$t('yuebao.eventDailyIncome'), '2': this.$t('yuebao.eventDuePrincipal'), '3': this.$t('yuebao.eventAdminIncome'), '4': this.$t('yuebao.eventAdminPrincipal'), '5': this.$t('yuebao.eventTransferOut') })[String(record.event_type)] || this.$t('yuebao.fundChange'); },
    recordAmount(record) { const value = record.amount; if (value == null || value === '') return '--'; const sign = ['0', '5'].includes(String(record.event_type)) ? '-' : '+'; return sign + this.$t('utils.moneyMark') + this.money(Math.abs(Number(value))); }
  }
};
</script>

<style lang="less" scoped>
* { box-sizing: border-box; }
.yuebao-page { min-width: 0; min-height: 100vh; padding-bottom: 24px; overflow-x: hidden; background: #f4f6f9; color: #202534; }
.hero { height: 254px; padding: 0 20px; color: #fff; background: #2672e8; }
.nav { display: flex; align-items: center; justify-content: space-between; height: 54px; h1 { font-size: 18px; font-weight: 600; } button { border: 0; color: #fff; background: transparent; } }
.back { width: 42px; margin-left: -12px; font-size: 34px; line-height: 40px; text-align: left; }.orders-link { width: 58px; padding: 0; font-size: 13px; text-align: right; }
.balance-label { margin-top: 20px; color: rgba(255,255,255,.76); font-size: 13px; text-align: center; }.balance { margin-top: 8px; overflow-wrap: anywhere; font-size: 35px; line-height: 44px; font-weight: 600; letter-spacing: .5px; text-align: center; }
.income-row { display: flex; margin-top: 27px; div { position: relative; display: flex; width: 50%; min-width: 0; flex-direction: column; align-items: center; } div + div:before { position: absolute; top: 3px; bottom: 3px; left: 0; width: 1px; background: rgba(255,255,255,.24); content: ''; } strong { max-width: 100%; overflow: hidden; font-size: 17px; text-overflow: ellipsis; } span { margin-top: 7px; color: rgba(255,255,255,.72); font-size: 12px; } }
main { margin-top: -18px; padding: 0 12px; }.panel { margin-bottom: 12px; padding: 16px; border: 1px solid #edf0f4; border-radius: 10px; background: #fff; }
.mode-tabs { display: flex; margin: -4px 0 18px; padding: 3px; border: 1px solid #e5e9ef; border-radius: 8px; background: #f6f7f9; button { width: 50%; height: 39px; border: 0; border-radius: 6px; background: transparent; color: #747b88; font-size: 15px; font-weight: 500; } .active { color: #fff; background: #2672e8; font-weight: 600; } }.mode-out .mode-tabs .active { background: #ef762f; }
.section-title { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; font-size: 15px; font-weight: 600; small { color: #9298a3; font-size: 11px; font-weight: 400; } }
.product-selector { display: flex; width: 100%; height: 62px; min-width: 0; align-items: center; gap: 8px; margin-bottom: 16px; padding: 0 12px; border: 1px solid #dfe4eb; border-radius: 8px; background: #fff; text-align: left; strong { min-width: 64px; max-width: 36%; overflow: hidden; color: #303541; font-size: 14px; text-overflow: ellipsis; white-space: nowrap; } i { flex: 0 0 auto; color: #8d94a0; font-size: 22px; font-style: normal; line-height: 1; } }.product-summary { min-width: 0; flex: 1; overflow: hidden; color: #777f8c; font-size: 11px; text-align: right; text-overflow: ellipsis; white-space: nowrap; }.product-selector-empty { justify-content: space-between; strong { max-width: none; color: #777f8c; font-weight: 500; } }
.empty-products { height: 44px; margin-bottom: 16px; padding: 0 12px; border: 1px solid #edf0f4; border-radius: 8px; background: #fafbfc; color: #999; font-size: 13px; line-height: 42px; text-align: center; }.product-sheet-list { max-height: 52vh; overflow-y: auto; -webkit-overflow-scrolling: touch; }.product-option { position: relative; display: flex; width: 100%; min-width: 0; height: 58px; align-items: center; padding: 0 44px 0 16px; border: 0; border-bottom: 1px solid #edf0f4; background: #fff; text-align: left; strong { min-width: 0; flex: 1; overflow: hidden; color: #303541; font-size: 14px; text-overflow: ellipsis; white-space: nowrap; } span { flex: 0 0 auto; margin-left: 10px; color: #777f8c; font-size: 12px; white-space: nowrap; } i { position: absolute; right: 18px; color: #2672e8; font-size: 16px; font-style: normal; } }.product-option.selected strong { color: #2672e8; }
.amount-head { display: flex; justify-content: space-between; align-items: flex-end; gap: 8px; color: #303541; font-size: 14px; span:last-child { min-width: 0; color: #777f8c; font-size: 11px; text-align: right; overflow-wrap: anywhere; } }.amount-input { display: flex; align-items: center; height: 58px; margin-top: 9px; padding: 0 12px; border: 1px solid #cfd5de; border-radius: 8px; background: #fff; transition: border-color .15s; > span { flex: 0 0 auto; font-size: 23px; } input { width: 0; min-width: 0; flex: 1; padding: 0 8px; border: 0; outline: 0; background: transparent; font-size: 25px; } button { flex: 0 0 auto; padding: 8px 0 8px 10px; border: 0; background: transparent; color: #2672e8; font-size: 13px; } }.amount-input:focus-within { border-color: #2672e8; }.mode-out .amount-input:focus-within { border-color: #ef762f; }.mode-out .amount-input button { color: #df6728; }.hint { margin-top: 8px; color: #9298a3; font-size: 11px; }.submit { width: 100%; height: 46px; margin-top: 19px; border: 0; border-radius: 8px; color: #fff; font-size: 16px; font-weight: 600; }.submit-in { background: #2672e8; }.submit-out { background: #ef762f; }.submit:disabled { opacity: .5; }
.holding { display: flex; align-items: center; justify-content: space-between; border-left: 3px solid #2672e8; div { display: flex; min-width: 0; flex-direction: column; span { color: #777f8c; font-size: 12px; } strong { margin-top: 7px; overflow-wrap: anywhere; font-size: 20px; } } button { flex: 0 0 auto; margin-left: 10px; padding: 9px 0 9px 10px; border: 0; background: #fff; color: #2672e8; font-size: 13px; font-weight: 600; } }.records-panel { padding-bottom: 7px; }.record { display: flex; justify-content: space-between; align-items: center; gap: 12px; padding: 13px 0; border-top: 1px solid #edf0f3; div { display: flex; min-width: 0; flex-direction: column; } strong { overflow: hidden; font-size: 14px; font-weight: 500; text-overflow: ellipsis; white-space: nowrap; } span { margin-top: 5px; color: #999faa; font-size: 11px; } b { flex: 0 0 auto; color: #333; font-size: 14px; }.plus { color: #e36031; } }.empty-records { padding: 12px 0; color: #999; text-align: center; }.rules { padding: 7px 6px; color: #858c98; h2 { margin-bottom: 9px; color: #555c69; font-size: 14px; } p { margin-bottom: 6px; font-size: 11px; line-height: 18px; } }
@media (max-width: 340px) { .hero { padding: 0 14px; }.balance { font-size: 31px; }.income-row strong { font-size: 15px; } main { padding: 0 8px; }.panel { padding: 13px; }.product-selector { gap: 5px; padding: 0 9px; strong { max-width: 32%; font-size: 13px; } }.product-summary { font-size: 10px; }.product-option { padding-left: 13px; span { margin-left: 6px; font-size: 11px; } }.amount-head { align-items: flex-start; flex-direction: column; }.amount-head span:last-child { text-align: left; }.amount-input input { font-size: 22px; } }
</style>
